export type IconCodeType =
  | "" //no icon
  | "skc"
  | "few"
  | "sct"
  | "bkn"
  | "ovc"
  | "wind_skc"
  | "wind_few"
  | "wind_sct"
  | "wind_bkn"
  | "wind_ovc"
  | "snow"
  | "rain_snow"
  | "rain_sleet"
  | "snow_sleet"
  | "fzra"
  | "rain_fzra"
  | "snow_fzra"
  | "sleet"
  | "rain"
  | "rain_showers"
  | "rain_showers_hi"
  | "tsra"
  | "tsra_sct"
  | "tsra_hi"
  | "tornado"
  | "hurricane"
  | "tropical_storm"
  | "dust"
  | "smoke"
  | "haze"
  | "hot"
  | "cold"
  | "blizzard"
  | "fog";

export type EntityType = {
  id?: string;
  name?: string | null;
  description?: string | null;
  font?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  subdomain?: string | null;
  customDomain?: string | null;
  createdAt?: string;
  updatedAt?: Date;
  updatedById?: string | null;
  logo: string;
  redirectToCourseFlag?: boolean | undefined;
};

export type CourseType = {
  logo: string | undefined;
  images: string[];
  id: string;
  name: string;
  description: string | null;
  address: string | null;
};

export type CourseImagesType = { logo?: string; images: string[] };

export type FullCourseType = {
  id: string;
  name: string;
  address: string | null;
  description: string | null;
  longitude: number | null;
  latitude: number | null;
  forecastApi: string | null;
  convenienceFeesFixedPerPlayer: number | null;
  markupFeesFixedPerPlayer: number | null;
  maxListPricePerGolferPercentage: number | undefined;
  openTime: string | null;
  closeTime: string | null;
  supportCharity: boolean;
  supportSensibleWeather: boolean;
  timezoneCorrection: number;
  highestListedTeeTime: number;
  lowestListedTeeTime: number;
  highestPrimarySaleTeeTime: number;
  lowestPrimarySaleTeeTime: number;
  supportedCharities?: SupportedCharity[];
  allowAuctions: boolean;
  supportsOffers?: boolean;
  supportsWatchlist?: boolean;
  supportsPromocode?: boolean;
  supportsWaitlist?: boolean;
  sellerFee?: number;
  buyerFee?: number;
  furthestDayToBook?: number;
  roundUpCharityId?: string;
  internalId?: string | null;
  supportsPlayerNameChange: boolean;
  websiteURL: string;
  courseOpenTime: number;
  courseCloseTime: number;
  supportsGroupBooking: boolean;
  timezoneISO?: string | undefined;
  groupBookingMinSize?: number;
  groupBookingMaxSize?: number;
  isOnlyGroupOfFourAllowed?: boolean;
  isAllowSpecialRequest?: boolean;
  isAllowClubRental?: boolean;
  groupStartTime: number | null;
  groupEndTime: number | null;
  isAllowCourseSwitching?: boolean;
  supportsSellingMerchandise: boolean;
  primaryMarketAllowedPlayers: number | null;
  primaryMarketSellLeftoverSinglePlayer: boolean | null;
  courseAllowedTimeToSellSlots: CourseAllowedTimeToSellSlot[] | null;
  bankingWidgetToShow?: string;
  sellerOfferFeePerPlayerPercentage: number;
  showWhiteLabelGolfDistrictLogoTop?: boolean;
  showWhiteLabelGolfDistrictLogoBottom?: boolean;
  showWhiteLabelEmailGolfDistrictLogo?: boolean;
  supportsEvents: boolean;
  css?: string | null;
  measurementGAId?: string | null;
  // Course-level GA setting (GA_SHOW_ENHANCED_EVENTS). When false/missing, the
  // course's own GA property receives only default events (page_view + gtag.js
  // auto-events); custom/enhanced events are withheld. The master property is
  // unaffected and always receives every event.
  showEnhancedGAEvents?: boolean;
  supportsUnsolicitedOffers: boolean;
  // NameYourPrice minimum offer, in cents, per player — resolved from the
  // NYP_MIN_OFFER_PRICE courseSetting. Nullable — when unset, the frontend falls
  // back to that day's actual green fee + cart fee for the course.
  nameYourPriceMinPerPlayer?: number | null;
  // The course's credit-card statement descriptor (courses.creditCardDescriptor,
  // max 20 chars). Shown on the order confirmation so the golfer recognises the
  // charge; when null the confirmation falls back to "Golf District".
  creditCardDescriptor?: string | null;
};

export type CourseAllowedTimeToSellSlot = {
  day: string;
  fromTime: number;
  toTime: number;
  primaryMarketAllowedPlayers: number;
  primaryMarketSellLeftoverSinglePlayer: boolean;
}

export type SupportedCharity = {
  charityDescription: string | null;
  charityName: string | null;
  charityId: string | null;
};

export type Course = FullCourseType & CourseImagesType;

export type WatchlistItem = {
  watchListId: string;
  watchListCreatedAt: string;
  ownedBy: string;
  teeTimeId: string;
  courseId: string;
  teeTimeExpiration: string;
  price: number;
  availableSpots: number;
  image: string;
  type: "FIRST_PARTY" | "SECOND_HAND";
  status: "LISTED" | "UNLISTED";
  bookingIds: string[];
  ownedById: string;
  listId?: string;
  minimumOfferPrice: number;
};

export interface BookingGroup {
  soldById: string;
  soldByName: string;
  soldByImage: string;
  availableSlots: number;
  pricePerGolfer: number;
  teeTimeId: string;
  date: string | null;
  time: number;
  listingId: string | null;
  userWatchListed: boolean;
  includesCart: boolean;
  teeTimeOwnedByCaller: boolean;
  listsPrice: string | null;
  teeTimeStatus: "LISTED" | "UNLISTED";
  minimumOfferPrice: number;
  firstHandPrice: number;
  golfers: string[];
  purchasedFor: number;
  bookings: string[];
  numberOfHoles?: number;
}

export type GroupedBookings = Record<string, BookingGroup>;

export enum TeeTimeType {
  FIRST_HAND = "FIRST_HAND",
  SECOND_HAND = "SECOND_HAND",
  UNLISTED = "UNLISTED",
  OFFER = "OFFER"
}

export enum BookingOfferStatus {
  SENT = 0,
  READ = 1,
  COUNTER = 2,
  ACCEPTED = 3,
  REJECTED = 4,
  EXPIRED = 5,
  RECEIVED = 6,
  WITHDRAWN = 7,
  INACTIVE = 8
}

export enum NameYourPriceOfferStatus {
  PENDING = 0,
  ACCEPTED = 1,
  DECLINED = 2,
  EXPIRED = 3,
  WITHDRAWN = 4,
}

export enum CHANNEL {
  WHITE_LABEL = 1,
  PHONE = 2,
}

export enum PAYMENTMODE {
  PRE_PAID = 1,
  NO_PAY = 2,
  PRE_AUTH = 3,
}

export enum smsVerificationStatus {
  PENDING = "PENDING",
  VERIFIED = "VERIFIED",
  EXPIRED = "EXPIRED",
  LOCKED = "LOCKED",
}

export type SearchObject = {
  soldById: string;
  soldByName: string;
  soldByImage: string;
  availableSlots: number;
  pricePerGolfer: number;
  greenFeeTaxPerPlayer?: number;
  cartFeeTaxPerPlayer?: number;
  teeTimeId: string;
  date: string; //day of tee time
  time: number; //military time
  includesCart: boolean;
  firstOrSecondHandTeeTime: TeeTimeType;
  isListed: boolean; //false if the booking is unlisted
  userWatchListed: boolean;
  weather: {
    temperature: number;
    shortForecast: string;
    name: string;
    iconCode: IconCodeType;
  };
  listingId?: string;
  listedSlots?: number;
  bookingIds?: string[];
  minimumOfferPrice?: number;
  firstHandPurchasePrice?: number;
  ownerId: string;
  groupId?: string;
  allowSplit?: boolean;
  totalMerchandiseAmount?: number;
  playerCountInGroup?: number;
  numberOfHoles?: number;
  defaultPartialPayment?: PartialPaymentConfig
};

export type PriceClassEntry = {
  id: string;
  name: string;
  isPublic: boolean;
  isActive: boolean;
  jsonConfig: string | null;
  teeTimeId: string;
  greenFee: number;
  cartFee: number;
  holes: 9 | 18;
  pricePerGolfer: number;
  salePolicyId: string | null;
  salePolicyAllowedPlayers: number | null;
  salePolicyMaxPlayers: number | null;
};

export type CombinedObject = {
  soldById: string;
  soldByName: string;
  soldByImage: string;
  availableSlots: number;
  pricePerGolfer: number;
  teeTimeId: string;
  date: string; //day of tee time
  time: number; //military time
  isOwned: boolean;
  includesCart: boolean;
  firstOrSecondHandTeeTime: TeeTimeType;
  isListed: boolean; //false if the booking is unlisted
  userWatchListed: boolean;
  listingId?: string;
  bookingIds: string[];
  minimumOfferPrice?: number;
  firstHandPurchasePrice?: number;
  listedSlots: number | null;
  groupId?: string;
  allowSplit: boolean;
  teeTimeIds?: string[];
  hasPriceClass?: boolean;
  priceClasses?: PriceClassEntry[];
  defaultHole: number;
  availableHoles?: (9 | 18)[];
  numberOfHoles?: number;
};

export type ReserveTeeTimeResponse = {
  bookingId: string;
  providerBookingId: string;
  status: string;
  isEmailSend: boolean;
  courseMembershipId?: string;
  playerCountForMemberShip?: string;
  isValidForCollectPayment: boolean;
  playTime?: string;
};

export type NotificationObject = {
  id: string;
  courseId: string;
  shortMessage: string;
  longMessage: string | null;
  displayType: string;
  startDate: string;
  endDate: string;
  bgColor: string;
  color: string;
};

export enum AuthenticationMethodEnum {
  EMAIL_PASSWORD = 1, // 00000001
  GOOGLE = 2, // 00000010
  LINKEDIN = 4, // 00000100
  FACEBOOK = 8, // 00001000
  INSTAGRAM = 16, // 00010000
  MICROSOFT = 32, // 00100000
  APPLE = 64, // 01000000
}

// ─── Type matching `courseEvent` table (DDL) ──────────────────────────────────
export interface CourseEvent {
  id: string;
  eventDate: Date;
  name: string;
  description: string;
  flyerAssetId: string | null;
  costPerPerson: number;
  markupPerPerson: number;
  minGroupSize: number;
  maxGroupSize: number;
  paymentModeId: string | null;
  paymentModeNid: number | null;
  taxPercentPerPerson: number;
  golfStartTime: number | null;
  flyerURL?: string | null;
}

export interface CourseEventBooking {
  id: string;
  courseEventId: string;
  userId: string;
  groupSize: number;
  name: string;
  eventDate: Date | string;
  bookedGroupSize: number;
  flyerURL?: string | null;
  slotsData?: {
    slotId: string;
    customerId: string;
    name: string | null;
    slotPosition: number;
  }[];
  paymentModeNid: number | null;
  isPartial: boolean;
  providerPaymentId: string | null;
}

export type PartialPaymentType =
  | 'FIXED_PER_PLAYER'
  | 'FIXED_TOTAL'
  | 'PERCENT_PER_PLAYER'
  | 'PERCENT_TOTAL';

export interface PartialPaymentConfig {
  enabled: boolean;
  type: PartialPaymentType | null;
  value: number | null;
}

export type SelectCheckoutCourseEvent = CourseEvent & {
  defaultPartialPayment: PartialPaymentConfig;
};
